import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Icon, Table} from '../index';
import Details from './InfoPanels/Details';
import PropTypes from 'prop-types';
import {_openModal} from '../reducers/modals';
import {formatter, openDocument, setSectionURI} from '../utils/utils';
import history from '../../history';
import {isAuthorized} from '../reducers/user';
import useQueryNumber from '../utils/useQueryNumber';

const DocumentVault = ({
  session,
  dataManager,
  allowCreatorActions = false,
  dataFilters,
  tableFilters,
  tableIconKeys,
  openModal,
  brand,
}) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeInfoPanel, setActiveInfoPanel] = useState('details');
  const [documentID] = useQueryNumber('documentID', null);

  useEffect(() => {
    if (documentID) {
      dataManager.gatherData({id: documentID, limit: 1})
        .then(res => {
          if (!res[0]) {
            openModal({
              type: 'MessageModal',
              title: 'Failed to Retrieve Document',
              body: 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at'
                + ` ${brand.clientServicesEmail} or ${formatter('phone')(brand.clientServicesPhone)}.`,
            });
          } else {
            openModal({
              type: 'DocumentDetails',
              document: res[0],
              dataManager: dataManager,
            });
          }
        })
        .catch(err => openModal({type: 'Error', err}));
    }
  }, [documentID]);

  dataManager.updateActiveDocument = document => setSelectedDocument(document);

  const infoPanelComponents = {details: Details};
  const infoPanelTitleMapping = {details: 'Details'};

  const setInfoRow = document => setSelectedDocument(document);

  const getInfoPanel = () => {
    const InfoPanel = infoPanelComponents[activeInfoPanel];

    return {
      title: infoPanelTitleMapping[activeInfoPanel],
      content: <InfoPanel
        document={selectedDocument}
        dataManager={dataManager}
      />,
      rowID: selectedDocument ? selectedDocument.id : null,
      setRow: setInfoRow,
    };
  };

  const shareRender = document => (
    <Icon svg='circleShare'
          action={() => {
            history.push(setSectionURI(`/${document.id}/sharing`));
          }}
          disabled={
            document.notShareable
            || ((document.cat === 'Report') && !document.assocHouseholdID)
            || (allowCreatorActions && (document.ownerID !== session.profile.id))
            || !!session.runAsInvestor
          }
          description={
            (allowCreatorActions && (document.ownerID !== session.profile.id))
              ? 'Since you are not the owner of this document, you cannot currently share this with other users.'
              : null
          }
    />
  );

  const detailsRender = document => (
    <Icon svg='details'
          action={() => {
            dataManager.toggleDisplayInfoPanel(true);
            setActiveInfoPanel('details');
            setSelectedDocument(document);
          }}/>);

  const previewRender = document => (
    <Icon svg='preview'
          action={() => openModal({
            componentName: 'ViewDocument',
            document: document,
            dataManager: dataManager,
          })}
          disabled={document.fileFormat !== 'html' && document.fileFormat !== 'pdf'}
    />
  );

  const downloadRender = document => (
    <Icon svg='download'
          action={() => openDocument(document, null, false, false, true)}
          disabled={document.fileFormat === 'html'}
    />
  );

  return (
    <Table dataManager={dataManager}
           columns={[
             {
               title: 'Document Name',
               dataPoint: 'title',
               type: 'text',
               minWidth: 160,
               isSortable: true,
             },
             {
               title: 'Type',
               dataPoint: 'type',
               type: 'text',
               minWidth: 60,
               isSortable: true,
             },
             {
               title: 'Date',
               dataPoint: 'createdAt',
               type: 'datetime',
               minWidth: 60,
               isSortable: true,
             },
             {
               title: 'Preview',
               dataPoint: 'preview',
               render: previewRender,
               type: 'custom',
               minWidth: 50,
               isSortable: false,
             },
             {
               title: 'Download',
               dataPoint: 'download',
               render: downloadRender,
               type: 'custom',
               minWidth: 50,
               isSortable: false,
             },
             !isAuthorized(['CPLO']) && {
               title: 'Share',
               dataPoint: 'isShared',
               render: shareRender,
               type: 'custom',
               minWidth: 50,
               isSortable: false,
             },
             {
               title: 'Details',
               dataPoint: 'details',
               render: detailsRender,
               type: 'custom',
               minWidth: 50,
               isSortable: false,
             },
           ].filter(c => !!c)}
           defaultSort={{
             column: 'createdAt',
             desc: true,
           }}
           searches={[
             {
               title: 'Document Name',
               name: 'title',
               isFuzzy: true,
             },
             {
               title: 'Type',
               name: 'type',
               isFuzzy: true,
             },
             {
               title: 'Tags',
               name: 'hasTag',
               isFuzzy: false,
             },
           ]}
           filters={tableFilters}
           filterValues={dataFilters}
           iconsKey={tableIconKeys}
           rowIdentifier={'id'}
           height={'500px'}
           infoPanel={getInfoPanel()}/>
  );
};

const mapStateToProps = state => ({
  brand: state.product.brand,
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentVault);

DocumentVault.propTypes = {
  /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    runAsInvestor: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * Data manager
   */
  dataManager: PropTypes.object.isRequired,
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  /**
   * Is true allows file creator to delete own files
   */
  allowCreatorActions: PropTypes.booleanValue,
  /**
   * Filters for dataManger
   */
  dataFilters: PropTypes.object.isRequired,
  /**
   * Filters for table side panel
   */
  tableFilters: PropTypes.array.isRequired,
  /**
   * Mappings for icon keys
   */
  tableIconKeys: PropTypes.array.isRequired,
  brand: PropTypes.object.isRequired,
};
